// ═════════════════════════════════════════════════════════════════════════════
// NHCX Coverage Eligibility API client
//
// Contract: COVERAGE_ELIGIBILITY_UI_INTEGRATION.md (repo root).
//
//   POST /coverageeligibility/check   → 200 { correlation_id, poll_url, … }
//   GET  /response/{correlation_id}   → 404 while waiting, 200 once the payer answers
//
// This is an ASYNCHRONOUS API. A 200 on /check means "submitted to NHCX", not
// "here is the eligibility answer" — the caller polls for the answer separately.
// A 404 from /response is the normal waiting state, never an error.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";

// ⚠ UNCONFIRMED HOST — the coverage-eligibility service is not checked out anywhere
// in this workspace and no .env var points at it; the integration guide only cites
// its Swagger UI at localhost:8000/docs. Point this at the real deployment before
// the Auto tab can work. Hardcoded base URL matches the other newer billing modules.
export const COVERAGE_BASE_URL = "http://localhost:8000";

// Org-Code / App-Mode are mandatory and case-insensitive. A missing header is a 422,
// a present-but-empty one a 400 — so an unauthenticated session fails loudly, which
// is what we want rather than a silent half-request. The lowercase session keys are
// the same fallbacks getAuthHeaders() in ../constants uses: sessions written by the
// older login path store those instead, and without them every call would 400.
function coverageHeaders() {
  return {
    "Content-Type": "application/json",
    "Org-Code":
      sessionStorage.getItem("ORG_CODE") || sessionStorage.getItem("orgCode") || "",
    "App-Mode":
      sessionStorage.getItem("APP_MODE") || sessionStorage.getItem("appMode") || "SaaS",
  };
}

/** POST the check. Resolves with the submission envelope (correlation_id inside). */
export async function submitEligibilityCheck(body) {
  const res = await axios.post(`${COVERAGE_BASE_URL}/coverageeligibility/check`, body, {
    headers: coverageHeaders(),
  });
  return res.data;
}

/**
 * Poll for the payer's answer. 404 is the documented "not yet" state, so it is
 * whitelisted as a success and mapped to { pending: true } rather than thrown.
 */
export async function pollEligibilityResponse(correlationId) {
  const res = await axios.get(
    `${COVERAGE_BASE_URL}/response/${encodeURIComponent(correlationId)}`,
    { headers: coverageHeaders(), validateStatus: (s) => s === 200 || s === 404 }
  );
  if (res.status === 404) return { pending: true };
  return res.data;
}

// Each missing_fields entry names the override that would fix it, e.g.
//   "facility_abdm_id — facility has no ABDM ID registered. Supply
//    override_facility_abdm_id or complete ABDM facility registration."
// The patient-identifier entry names two (aadhaar OR abha) — either one satisfies it.
// patient_name names none: it can only be fixed in the hospital record.
const OVERRIDE_TOKEN = /override_[a-z_]+/g;

function parseMissingFields(list) {
  return (list || []).map((raw) => {
    const message = String(raw);
    return {
      message,
      overrides: [...new Set(message.match(OVERRIDE_TOKEN) || [])],
    };
  });
}

const STATUS_MESSAGES = {
  400: "A required header was empty — the session may have lost its facility context.",
  404: "Admission not found on the eligibility service.",
  500: "The eligibility service failed to build the request. Quote the API call id to support.",
  502: "The NHCX gateway is unreachable. Try again in a moment.",
  503: "The eligibility service database is unavailable. Try again in a moment.",
};

/**
 * Flatten every documented failure shape into one object the UI can render:
 *   { message, fieldErrors: [{field, message}], missing: [{message, overrides[]}] }
 *
 * 422 arrives in two shapes — a Pydantic array (field format wrong) or a Step-C
 * object (data missing from both the DB and the overrides). The object shape lists
 * everything missing at once, so one round trip reveals the full set of inputs.
 */
export function normalizeCheckError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  if (status === 422 && Array.isArray(detail)) {
    return {
      status,
      message: "Some fields were rejected by the eligibility service.",
      fieldErrors: detail.map((d) => ({
        field: (d?.loc || []).filter((p) => p !== "body").join("."),
        message: d?.msg || "Invalid value",
      })),
      missing: [],
    };
  }

  if (status === 422 && detail && typeof detail === "object") {
    return {
      status,
      message: detail.message || "Required details are missing for this admission.",
      fieldErrors: [],
      missing: parseMissingFields(detail.missing_fields),
    };
  }

  return {
    status,
    message:
      (typeof detail === "string" && detail) ||
      STATUS_MESSAGES[status] ||
      err?.message ||
      "Failed to submit the eligibility check.",
    fieldErrors: [],
    missing: [],
  };
}
